import { z } from 'zod';
import { c } from '../../contract';

export const userProfile = z
  .object({
    username: z.string().nullable(),
    avatarUrlPath: z.string().nullable(),
    isUsernameAutoGenerated: z.boolean().nullable(),
    profileUpdatePromptCount: z.number().nullable(),
    isUserHasActiveSubscription: z.boolean().nullable(),
    dateOfBirth: z.string().nullable(),
  })
  .optional();

export const userProfileRouter = c.router(
  {
    getProfileData: {
      summary: 'Get user profile data',
      method: 'GET',
      path: '/:userId',
      pathParams: z.object({
        userId: z.coerce.string(),
      }),
      responses: {
        200: z.object({
          userProfileData: userProfile,
          message: z.string(),
        }),
      },
    },
    addProfileData: {
      summary: 'Add user profile data',
      method: 'POST',
      path: '/add-profile',
      body: z.object({
        username: z.string(),
        avatarUrlPath: z.string().optional(),
        isUsernameAutoGenerated: z.boolean().optional(),
        profileUpdatePromptCount: z.number().optional(),
      }),
      responses: {
        201: z.object({
          userProfileData: userProfile,
        }),
      },
    },
    updateProfile: {
      summary: 'Update profile data',
      method: 'PUT',
      path: '/:userId',
      pathParams: z.object({
        userId: z.coerce.string(),
      }),
      body: z.object({
        username: z.string().optional(),
        avatarUrlPath: z.string().optional(),
        isUsernameAutoGenerated: z.boolean().optional(),
        profileUpdatePromptCount: z.number().optional(),
      }),
      responses: {
        200: z.object({
          userProfileData: userProfile,
        }),
        401: z.object({
          error: z.string(),
        }),
      },
    },
    removeAvatar: {
      summary: 'Remove avatar',
      method: 'DELETE',
      path: '/:userId/avatar',
      pathParams: z.object({
        userId: z.coerce.string(),
      }),
      responses: {
        200: z.object({
          userProfileData: userProfile,
        }),
        401: z.object({
          error: z.string(),
        }),
      },
    },
    checkUsernameUnique: {
      summary: 'Check if username is unique',
      method: 'GET',
      path: '/check-username',
      query: z.object({
        username: z.string(),
      }),
      responses: {
        200: z.object({
          isUnique: z.boolean(),
        }),
      },
    },
    setMarketingSegmentId: {
      summary: 'Set the marketingSegmentId for the user',
      method: 'POST',
      path: '/marketing-segment-id',
      body: z.object({
        marketingSegmentId: z.string(),
      }),
      responses: {
        200: z.object({
          marketingSegmentId: z.string().or(z.null()),
        }),
      },
    },
    setIsSubscribed: {
      summary: 'Set the isSubscribed flag for the user',
      method: 'POST',
      path: '/is-subscribed',
      body: z.object({
        isSubscribed: z.boolean(),
      }),
      responses: {
        200: z.object({
          isSubscribed: z.boolean().or(z.null()),
        }),
      },
    },
    getMarketingSegmentId: {
      summary: 'Get the marketingSegmentId for the user',
      method: 'GET',
      path: '/marketing-segment-id',
      responses: {
        200: z.object({
          marketingSegmentId: z.string().or(z.null()),
        }),
        404: z.string(),
      },
    },
    uploadAvatar: {
      summary: 'Upload user avatar',
      method: 'POST',
      path: '/upload-avatar',
      body: z.any(),
      responses: {
        201: z.object({
          message: z.string(),
          avatarUrlPath: z.string(),
        }),
        400: z.object({
          error: z.string(),
        }),
        500: z.object({
          error: z.string(),
        }),
      },
    },
    searchProfilesByUsername: {
      summary: 'Search user profiles by partial username',
      method: 'GET',
      path: '/search-profiles',
      query: z.object({
        username: z.string(),
      }),
      responses: {
        200: z.object({
          users: z.array(userProfile),
        }),
      },
    },
    setRevcatOfferingId: {
      summary: 'Set the revcat offering id for the user',
      method: 'POST',
      path: '/revcat-offering-id',
      body: z.object({
        revcatOfferingId: z.string(),
      }),
      responses: {
        200: z.object({
          revcatOfferingId: z.string().nullable(),
        }),
      },
    },
    getRevcatOfferingId: {
      summary: 'Get the revcat offering id for the user',
      method: 'GET',
      path: '/revcat-offering-id',
      responses: {
        200: z.object({
          revcatOfferingId: z.string().nullable(),
        }),
      },
    },
  },
  {
    pathPrefix: '/user-profiles',
  },
);
